import {
  Checkbox,
  DefaultButton,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  IDragDropContext,
  IDragDropEvents,
  mergeStyleSets,
  Selection,
  Stack,
} from "@fluentui/react";
import React from "react";
import { connect } from "react-redux";
import { v4 } from "uuid";

import { popup } from "../../../../../addons/Popup";
import { RootState } from "../../../../../App/store";
import { addIcon, deleteIcon } from "../../../../../utils/icons";
import { theme } from "../../../../../utils/theme";
import { actions, getFormVariables } from "../../../slice";
import { getFormState, Product } from "../../../slice/newFormReducers";
import { connectRender, detailsListNoPaddingStyles } from "../utils";
import CountInput from "./CountInput";
import DescriptionInput from "./DescriptionInput";
import IndexDisplay from "./IndexDisplay";
import SelectorInput from "./SelectorInput";
import SellPriceInput from "./SellPriceInput";
import ShipmentCheckbox from "./ShipmentCheckbox";
import Summary from "./Summary";
import TotalPriceInput from "./TotalPriceInput";

type ProductListProps = {
  shipmentEditMode?: boolean;
  items: string[];
};

type ProductListState = {
  deleteSelected: string[];
};

class ProductList extends React.Component<ProductListProps, ProductListState> {
  private _columns: IColumn[] | undefined;
  private _dragDropEvents: IDragDropEvents | undefined;
  private _draggedItem: string | undefined;
  private selection: Selection;

  constructor(props: ProductListProps) {
    super(props);

    this.state = {
      deleteSelected: [],
    };
    this.selection = new Selection({
      onSelectionChanged: () => {
        this.setState({
          deleteSelected: this.selection.getSelection() as string[],
        });
      },
    });
  }

  componentDidMount() {
    this.componentDidUpdate(this.props);
  }

  componentDidUpdate(prevProps: ProductListProps) {
    if (this.props.items.length === 0) {
      addNewProductRow();
    }
  }

  render() {
    return (
      <Stack tokens={stackTokens}>
        {this.renderProductsList()}
        {!this.props.shipmentEditMode && (
          <Stack horizontal>
            {this.renderButtons()}
            {this.renderSummary()}
          </Stack>
        )}
      </Stack>
    );
  }

  renderProductsList() {
    return (
      <DetailsList
        styles={{ root: { overflow: "hidden" } }}
        items={this.props.items}
        columns={this.columns}
        selection={this.selection}
        selectionZoneProps={{
          selection: this.selection,
          disableAutoSelectOnInputElements: true,
        }}
        layoutMode={DetailsListLayoutMode.fixedColumns}
        className={detailsListNoPaddingStyles}
        dragDropEvents={this.dragDropEvents}
      />
    );
  }

  renderButtons() {
    return (
      <Stack grow horizontal horizontalAlign="start" tokens={stackTokens}>
        <DefaultButton
          iconProps={addIcon}
          text="Satır Ekle"
          onClick={addNewProductRow}
        />
        <DefaultButton
          iconProps={deleteIcon}
          disabled={this.state.deleteSelected.length === 0}
          text={`${this.state.deleteSelected.length} Satırı Sil`}
          onClick={this.deleteSelectedRows.bind(this)}
        />
      </Stack>
    );
  }

  renderSummary() {
    return (
      <div style={{ width: "35%", maxWidth: "600px" }}>
        <Summary />
      </div>
    );
  }

  async deleteSelectedRows() {
    const { deleteSelected } = this.state;
    const { cancelled } = await popup({
      title: "Satırları Sil",
      message: `${deleteSelected.length} satırı silmek istediğinize emin misiniz?`,
      buttonText: "Sil",
    });

    if (cancelled) {
      return;
    }

    deleteSelected.forEach((uuid) => {
      actions.removeFormProduct(uuid);
    });
  }

  get columns() {
    if (this._columns) {
      return this._columns;
    }

    const cols: IColumn[] = [
      {
        key: "index",
        minWidth: 16,
        maxWidth: 16,
        name: "#",
        iconName: "NumberSymbol",
        isIconOnly: true,
        onRender: (uuid, index) =>
          typeof index === "number" && (
            <IndexDisplay index={index + 1} uuid={uuid} />
          ),
      },
      // {
      //   key: "uuidd",
      //   minWidth: 16,
      //   maxWidth: 800,
      //   flexGrow: 10,
      //   name: "UUID",
      //   onRender: connectRender((uuid) => <span>{uuid.toString()}</span>),
      // },
      {
        key: "product",
        minWidth: 16,
        maxWidth: 800,
        flexGrow: 10,
        name: "Ürün Açıklaması",
        onRender: connectRender((uuid) => (
          <SelectorInput
            uuid={uuid}
            shipmentEditMode={this.props.shipmentEditMode}
          />
        )),
      },
      {
        key: "description",
        minWidth: 16,
        maxWidth: 800,
        flexGrow: 4.5,
        name: "Detay",
        onRender: connectRender((uuid) => (
          <DescriptionInput
            uuid={uuid}
            shipmentEditMode={this.props.shipmentEditMode}
          />
        )),
      },
      {   
        key: "isShipped",
        minWidth: 16,
        flexGrow: 1.5,
        name: "Teslim",
        onRender: connectRender((uuid) => <ShipmentCheckbox uuid={uuid} />),
      },
      {
        key: "count",
        minWidth: 40,
        maxWidth: 800,
        flexGrow: 1,
        name: "Adet",
        onRender: connectRender((uuid) => (
          <CountInput
            uuid={uuid}
            shipmentEditMode={this.props.shipmentEditMode}
          />
        )),
        data: { displayOnShipmentEdit: false },
      },
      {
        key: "sellPrice",
        minWidth: 70,
        maxWidth: 800,
        flexGrow: 2.5,
        name: "Birim Fiyatı",
        onRender: connectRender((uuid) => <SellPriceInput uuid={uuid} />),
        data: { displayOnShipmentEdit: false },
      },
      {
        key: "totalSellPrice",
        minWidth: 70,
        maxWidth: 800,
        flexGrow: 2.5,
        name: "Toplam",
        onRender: connectRender((uuid) => <TotalPriceInput uuid={uuid} />),
        data: { displayOnShipmentEdit: false },
      }
    ];

    this._columns = cols
      .filter(
        (x) =>
          x.data?.displayOnShipmentEdit !== false ||
          !this.props.shipmentEditMode
      )
      .filter(
        (x) =>
          x.data?.displayOnShipmentEdit !== true || this.props.shipmentEditMode
      );

    return this._columns;
  }

  get dragDropEvents() {
    if (this._dragDropEvents) {
      return this._dragDropEvents;
    }

    this._dragDropEvents = {
      canDrop: (
        dropContext?: IDragDropContext,
        dragContext?: IDragDropContext
      ) => {
        return true;
      },
      canDrag: (uuid?: string, s?: any) => true,
      onDragEnter: (item?: string, event?: DragEvent) => styles.dragEnter,
      onDragLeave: (item?: string, event?: DragEvent) => {},
      onDrop: (item?: string, event?: DragEvent) => {
        if (item && this._draggedItem) {
          const formState = getFormState();
          if (!formState.products) return;

          const formVariables = getFormVariables();
          const _items = [...Object.keys(formVariables.products || {})].filter(
            (uuid) => uuid !== this._draggedItem
          );
          const dropItemIndex = _items.indexOf(item);

          _items.splice(dropItemIndex, 0, this._draggedItem);

          const newItems: { [uuid: string]: Product } = {};

          for (let i = 0; i < _items.length; i++) {
            const uuid = _items[i];
            newItems[uuid] = formState.products[uuid];
          }

          actions.setFormVariable(["products", newItems]);
        }
      },
      onDragStart: (
        item?: string,
        itemIndex?: number,
        selectedItems?: string[],
        event?: MouseEvent
      ) => {
        const moveIcon = document.getElementById(`rowMoveIcon-${item}`);

        if (moveIcon) {
          this._draggedItem = item;
        } else {
          this._draggedItem = undefined;
          event?.preventDefault();
        }
      },
      onDragEnd: (item?: string, event?: DragEvent) => {
        this._draggedItem = undefined;
      },
    };

    return this._dragDropEvents;
  }
}

export default connect((state: RootState) => {
  const fv = state.currentForm.newForms[state.currentForm.editingNewFormUUID];

  return {
    items: Object.keys(fv.products || {}),
  };
})(ProductList);

export const addNewProductRow = () => {
  const id = v4();

  actions.setFormProductVariable([id, "uuid", id]);
  actions.setFormProductVariable([id, "count", 1]);
  actions.setFormProductVariable([id, "KDV", 8]);
  actions.setFormProductVariable([id, "isShipped", false]);
};

const stackTokens = { childrenGap: 15 };
const styles = mergeStyleSets({
  dragEnter: {
    backgroundColor: theme.palette.neutralLight,
  },
});
